/**
 * Calculates the next follow-up time and notes for "not answered" call status.
 * @param {number} currentCallCount - The number of previous calls to this lead.
 * @returns {object} An object containing isoNextFollowUp and notesSuffix.
 */
export const calculateNextFollowUpDetailsForNoAnswer = (currentCallCount = 0) => {
  // console.log('[DEBUG] calculateNextFollowUpDetailsForNoAnswer called with currentCallCount:', currentCallCount);
  // console.log('[DEBUG] Browser timezone:', Intl.DateTimeFormat().resolvedOptions().timeZone);
  // console.log('[DEBUG] Browser timezone offset (minutes):', new Date().getTimezoneOffset());

  // User's local time (as seen by browser)
  const browserNow = new Date();
  // console.log('[DEBUG] Browser current time:', browserNow.toString());
  // console.log('[DEBUG] Browser current ISO:', browserNow.toISOString());

  // Potential source 1: Hours calculation
  let hoursToAdd = 4; // default (< 3 calls)
  let timeDescription = '4 hours';
  
  if (currentCallCount >= 8) {
    hoursToAdd = 24 * 7; // 1 week
    timeDescription = '1 week';
  } else if (currentCallCount >= 5) {
    hoursToAdd = 48; // 48 hours (2 days)
    timeDescription = '48 hours';
  } else if (currentCallCount >= 3) {
    hoursToAdd = 12; // 12 hours
    timeDescription = '12 hours';
  }
  // console.log('[DEBUG] Selected hours to add:', hoursToAdd, 'for description:', timeDescription);

  // Potential source 2: Date creation and manipulation
  // Create initial date
  const now = new Date();
  // console.log('[DEBUG] Initial now date object:', now.toString());
  
  // Create follow-up date
  let followUpDate = new Date(now);
  // console.log('[DEBUG] Initial followUpDate before adding time:', followUpDate.toString());
  
  // Add time based on hours calculation
  if (hoursToAdd === 24 * 7) { // 1 week
    followUpDate.setDate(now.getDate() + 7);
    // console.log('[DEBUG] Adding 7 days');
  } else if (hoursToAdd === 48) { // 2 days
    followUpDate.setDate(now.getDate() + 2); 
    // console.log('[DEBUG] Adding 2 days');
  } else { // hours
    followUpDate.setHours(now.getHours() + hoursToAdd);
    // console.log('[DEBUG] Adding', hoursToAdd, 'hours');
  }
  
  // console.log('[DEBUG] followUpDate after adding time:', followUpDate.toString());
  // console.log('[DEBUG] followUpDate ISO after adding time:', followUpDate.toISOString());
  
  // Potential source 3: Display time formatting
  const displayTime = followUpDate.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });
  // console.log('[DEBUG] Display time (simple format):', displayTime);
  
  // Potential source 4: Timestamp conversion method for ISO
  // Method A: Direct ISO conversion
  const directIsoString = followUpDate.toISOString();
  // console.log('[DEBUG] Method A - Direct ISO string:', directIsoString);
  
  // Method B: With timezone offset adjustment (same as 'Yes' option)
  const isoDate = new Date(followUpDate); 
  isoDate.setMinutes(isoDate.getMinutes() - isoDate.getTimezoneOffset());
  const offsetAdjustedIso = isoDate.toISOString();
  // console.log('[DEBUG] Method B - Timezone offset adjustment');
  console.log('  Original minutes:', followUpDate.getMinutes());
  console.log('  Timezone offset:', isoDate.getTimezoneOffset(), 'minutes');
  console.log('  Adjusted minutes:', isoDate.getMinutes());
  console.log('  Result ISO:', offsetAdjustedIso);
  
  // Method C: Creating an ISO date directly for a known IST time
  // First, explicitly create a date representing the desired IST time
  const istFollowUpTime = new Date(followUpDate.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  // console.log('[DEBUG] Method C - IST follow-up time:', istFollowUpTime.toString());
  
  // Then convert back to UTC by subtracting the 5.5 hour offset
  const utcDate = new Date(istFollowUpTime.getTime() - (5.5 * 60 * 60 * 1000));
  const manualUtcAdjustedIso = utcDate.toISOString();
  console.log('[DEBUG] Method C - Manual UTC adjustment: subtract 5.5 hours');
  console.log('  Result ISO:', manualUtcAdjustedIso);
  
  // Method D: Force date construction with UTC interpretation of components
  // Extract time parts from our target follow-up date
  const targetYear = followUpDate.getFullYear();
  const targetMonth = followUpDate.getMonth();
  const targetDate = followUpDate.getDate();
  const targetHours = followUpDate.getHours();
  const targetMinutes = followUpDate.getMinutes();
  
  // Construct a Date with the same components, interpreted as UTC
  const utcConstructedDate = new Date(Date.UTC(
    targetYear, targetMonth, targetDate, targetHours, targetMinutes
  ));
  const utcConstructedIso = utcConstructedDate.toISOString();
  console.log('[DEBUG] Method D - UTC component construction:');
  console.log('  Components:', { targetYear, targetMonth, targetDate, targetHours, targetMinutes });
  console.log('  Result ISO:', utcConstructedIso);
  
  // Potential source 5: Display vs storage format
  // Compare the display time with how different ISO strings would be displayed
  const fullDisplayTime = followUpDate.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
  console.log('[DEBUG] Full display time for notes:', fullDisplayTime);
  
  console.log('[DEBUG] How each ISO method would display in IST:');
  console.log('  Method A:', new Date(directIsoString).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }));
  console.log('  Method B:', new Date(offsetAdjustedIso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }));
  console.log('  Method C:', new Date(manualUtcAdjustedIso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }));
  console.log('  Method D:', new Date(utcConstructedIso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }));
  
  // Use Method A (direct ISO) since we want the actual time without timezone adjustments
  // This fixes the issue where follow-up time was being offset by an additional 5:30 hours
  const chosenMethod = 'A';
  const isoNextFollowUp = directIsoString;
  console.log('[DEBUG] CHOSEN METHOD:', chosenMethod);
  console.log('[DEBUG] Final isoNextFollowUp value:', isoNextFollowUp);

  return {
    isoNextFollowUp,
    notesSuffix: `Call not picked up - Auto rescheduled to ${fullDisplayTime} (${timeDescription} later based on ${Number(currentCallCount)} previous calls)`,
  };  
};